import { useEffect, useState, useMemo } from 'react';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Select } from 'foundations-components/transitional/components/Select';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import CompanionFooter from './CompanionFooter';
import InteractiveContentError from './InteractiveContentError';
import InteractiveEducationLoading from './InteractiveEducationLoading';
import UsageTracker from '../../../utils/UsageTracker';
import { useGenerateIndustryUseCaseQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/IndustryUseCaseRpc.hooks';
import { useGetAllAvailableIndustriesQuery } from '../../../../__generated__/chirp/com/hubspot/blueprint/industry/rpc/IndustryRpc.hooks';
import { useUserIndustry } from '../hooks/useUserIndustry';
import { useGetAiAccessControls } from '../../aiControl/aiAccessQueries';
import { handleApolloErrors } from '../../../utils/errorUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const QUERY_TIMEOUT = 300000;
const SCROLL_CONTAINER_HEIGHT = '450px';
const AdaptToIndustryTab = ({
  additionalTracking,
  contentId,
  contentPortalId,
  contentType,
  videoId,
  videoTitle
}) => {
  var _useCase$sections;
  const {
    aiAccessControls
  } = useGetAiAccessControls();
  const aiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const {
    data: availableIndustriesData
  } = useGetAllAvailableIndustriesQuery();
  const {
    industry: userSettingsIndustry,
    label: userSettingsIndustryLabel,
    loading: userSettingsIndustryLoading
  } = useUserIndustry();
  const [selectedIndustry, setSelectedIndustry] = useState();
  const handleIndustryChange = event => {
    var _event$target, _event$target2;
    setSelectedIndustry(event === null || event === void 0 || (_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value);
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, additionalTracking, {
      selectedIndustry: event === null || event === void 0 || (_event$target2 = event.target) === null || _event$target2 === void 0 ? void 0 : _event$target2.value,
      action: 'ai-adapt-to-industry-industry-selected'
    }));
  };
  const currentIndustry = userSettingsIndustry || selectedIndustry;
  const isIndustrySet = !!currentIndustry;
  const industryOptions = useMemo(() => {
    var _availableIndustriesD;
    return (availableIndustriesData === null || availableIndustriesData === void 0 || (_availableIndustriesD = availableIndustriesData.industries) === null || _availableIndustriesD === void 0 ? void 0 : _availableIndustriesD.filter(industryOption => readUnhandledEnumCase(industryOption.industry) !== 'OTHER').map(industryOption => ({
      text: industryOption.label,
      value: readUnhandledEnumCase(industryOption.industry)
    }))) || [];
  }, [availableIndustriesData === null || availableIndustriesData === void 0 ? void 0 : availableIndustriesData.industries]);
  const getIndustryLabel = () => {
    var _availableIndustriesD2;
    if (!selectedIndustry) {
      return '';
    }
    return availableIndustriesData === null || availableIndustriesData === void 0 || (_availableIndustriesD2 = availableIndustriesData.industries) === null || _availableIndustriesD2 === void 0 || (_availableIndustriesD2 = _availableIndustriesD2.find(industryOption => industryOption.industry === selectedIndustry)) === null || _availableIndustriesD2 === void 0 ? void 0 : _availableIndustriesD2.label;
  };
  const getBadgeText = () => {
    if (!isIndustrySet) {
      return I18n.text('learningCenter.interactiveEducation.adaptToIndustry.defaultIndustryText');
    }
    const industryLabel = userSettingsIndustryLabel || getIndustryLabel();
    return I18n.text('learningCenter.interactiveEducation.adaptToIndustry.industryBadgeText', {
      industry: industryLabel
    });
  };
  const onGenerateCompleted = __data => {
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, additionalTracking, {
      selectedIndustry: (selectedIndustry === null || selectedIndustry === void 0 ? void 0 : selectedIndustry.toString()) || '',
      action: 'ai-adapt-to-industry-generated-use-case'
    }));
  };
  const {
    data,
    loading,
    error,
    refetch
  } = useGenerateIndustryUseCaseQuery({
    variables: {
      videoPortalId: contentPortalId,
      videoId: videoId,
      industry: selectedIndustry
    },
    onCompleted: onGenerateCompleted,
    context: {
      timeout: QUERY_TIMEOUT
    },
    skip: !aiEnabled || !videoId || !contentPortalId || !isIndustrySet,
    onError: err => handleApolloErrors({
      error: err,
      loggingTitle: 'learningCenter.interactiveEducation.errors.adaptToIndustryGenerationError'
    })
  });
  const useCase = data === null || data === void 0 ? void 0 : data.useCase;
  const handleRetry = () => void refetch();
  useEffect(() => {
    UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, additionalTracking, {
      action: 'ai-adapt-to-industry-tab-rendered'
    }));
  }, [additionalTracking]);
  useEffect(() => {
    if (!userSettingsIndustry && !userSettingsIndustryLoading) {
      UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({}, additionalTracking, {
        action: 'ai-adapt-to-industry-select-ui-rendered'
      }));
    }
  }, [additionalTracking, userSettingsIndustry, userSettingsIndustryLoading, userSettingsIndustryLabel]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      overflow: "hidden",
      paddingBlockStart: "700",
      paddingInline: "700",
      children: error ? /*#__PURE__*/_jsx(InteractiveContentError, {
        handleRetry: handleRetry,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.adaptToIndustry.title"
        })
      }) : loading ? /*#__PURE__*/_jsx(InteractiveEducationLoading, {
        carouselTexts: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.industry.fact1"
        }, "fact1"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.industry.fact2"
        }, "fact2"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.industry.fact3"
        }, "fact3"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.industry.fact4"
        }, "fact4")],
        mainText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.buildingIndustry"
        })
      }) : /*#__PURE__*/_jsxs(ScrollContainer, {
        blockSize: SCROLL_CONTAINER_HEIGHT,
        maxHeight: 450,
        paddingBlockEnd: "700",
        children: [/*#__PURE__*/_jsx(Badge, {
          marginBlockEnd: "400",
          use: "lorax",
          children: getBadgeText()
        }), userSettingsIndustryLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
          layout: "centered"
        }), !isIndustrySet && !userSettingsIndustryLoading && /*#__PURE__*/_jsxs(View, {
          "data-test-id": "industry-selection-container",
          marginBlockEnd: "600",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h3",
            color: "text-core-default",
            marginBlockEnd: "400",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.interactiveEducation.adaptToIndustry.selectLabel"
            })
          }), /*#__PURE__*/_jsx(View, {
            maxInlineSize: "700px",
            children: /*#__PURE__*/_jsx(Select, {
              anchorType: "input",
              "data-test-id": "industry-selection-selector",
              onChange: handleIndustryChange,
              options: industryOptions,
              placeholder: I18n.text('learningCenter.interactiveEducation.adaptToIndustry.selectPlaceholder')
            })
          })]
        }), isIndustrySet && videoTitle && /*#__PURE__*/_jsxs(Text, {
          as: "h3",
          color: "text-core-default",
          marginBlockEnd: "400",
          children: [userSettingsIndustryLabel || getIndustryLabel(), ": ", videoTitle]
        }), useCase === null || useCase === void 0 || (_useCase$sections = useCase.sections) === null || _useCase$sections === void 0 ? void 0 : _useCase$sections.map((section, index) => {
          var _section$bulletPoints;
          return /*#__PURE__*/_jsx(View, {
            "data-test-id": `industry-section-${index}`,
            paddingBlockEnd: "200",
            children: /*#__PURE__*/_jsxs(View, {
              marginBlockEnd: "200",
              paddingBlock: "200",
              children: [/*#__PURE__*/_jsx(View, {
                alignItems: "baseline",
                gap: "300",
                marginBlockEnd: "400",
                children: /*#__PURE__*/_jsx(Text, {
                  as: "h7",
                  color: "text-core-default",
                  children: section.sectionName
                })
              }), /*#__PURE__*/_jsx(List, {
                marginBlockStart: "200",
                paddingInlineStart: "800",
                styled: true,
                children: (_section$bulletPoints = section.bulletPoints) === null || _section$bulletPoints === void 0 ? void 0 : _section$bulletPoints.map((bullet, i) => /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(Text, {
                    color: "text-core-default",
                    children: bullet
                  })
                }, i))
              })]
            })
          }, section.sectionName);
        })]
      })
    }), /*#__PURE__*/_jsx(CompanionFooter, {
      contentType: contentType,
      correlationId: data === null || data === void 0 ? void 0 : data.correlationId,
      featureId: "ADAPT_TO_INDUSTRY",
      primaryContentId: contentId,
      trackingAction: "AI_ADAPT_TO_INDUSTRY_FEEDBACK"
    })]
  });
};
export default AdaptToIndustryTab;